import { clearAuthCookies } from "./cookies";

const ACCESS_TOKEN_KEY = "zoop_session_token";

function readAccessToken(): string {
  try {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function getAccessToken(): string {
  return readAccessToken();
}

export function setAccessToken(token: string): void {
  const value = token.trim();
  try {
    if (value) {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, value);
    } else {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  } catch {
    // Private mode may block sessionStorage; Authorization header is skipped.
  }
}

export function authorizationHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type ApiLoginUser = {
  user_pid?: string;
  user_id?: string;
  service_id?: string;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  email_verified?: boolean;
  phone_verified?: boolean;
  user_disabled?: boolean;
  disabled_reason?: string;
  password_autogenerated?: boolean;
  role?: string;
  organisation_pid?: string;
  organisation_id?: string;
  onboarding_status?: string;
  consent_provided?: boolean;
  consent?: string;
  configs?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  login_attempts?: number;
  blocked_until?: number;
  is_active?: boolean;
  is_deleted?: boolean;
  created_at?: number;
  updated_at?: number;
};

export type StoredUser = {
  _id: string;
  service_id?: string;
  user_name: string;
  user_email: string;
  user_phone?: string;
  email_verified?: boolean;
  phone_verified?: boolean;
  user_disabled?: boolean;
  role?: string;
  roles?: Array<{ org_id: string }>;
  organisation_pid?: string;
  organisation_id?: string;
  onboarding_status?: string;
  consent_provided?: boolean;
  metadata?: Record<string, unknown>;
  created_at?: number;
  updated_at?: number;
};

export function normalizeLoginUser(user: ApiLoginUser): StoredUser {
  const id = user.user_pid || user.user_id || "";
  const orgId = user.organisation_pid || user.organisation_id || "";

  return {
    _id: id,
    service_id: user.service_id,
    user_name: user.user_name || "",
    user_email: user.user_email || "",
    user_phone: user.user_phone,
    email_verified: user.email_verified,
    phone_verified: user.phone_verified,
    user_disabled: user.user_disabled,
    role: user.role,
    roles: orgId ? [{ org_id: orgId }] : [],
    organisation_pid: orgId,
    organisation_id: orgId,
    onboarding_status: (user.onboarding_status || "").trim().toLowerCase(),
    consent_provided: user.consent_provided,
    metadata: user.metadata,
    created_at: user.created_at,
    updated_at: user.updated_at,
  };
}

export function getStoredUserId(user: unknown): string {
  if (!user || typeof user !== "object") return "";

  const record = user as Record<string, string | undefined>;
  return record._id || record.user_id || record.user_pid || "";
}

export function getCurrentUserId(): string {
  try {
    const raw = localStorage.getItem("@user");
    if (!raw) return "";
    return getStoredUserId(JSON.parse(raw));
  } catch {
    return "";
  }
}

/** Resolves organisation PID from session user, syncing localStorage when needed. */
export function getCurrentOrganisationId(): string {
  try {
    const raw = localStorage.getItem("@user");
    if (raw) {
      const user = JSON.parse(raw) as StoredUser;
      const orgId =
        user.organisation_pid ||
        user.organisation_id ||
        user.roles?.[0]?.org_id ||
        "";
      if (orgId) {
        if (localStorage.getItem("organisation_id") !== orgId) {
          localStorage.setItem("organisation_id", orgId);
        }
        return orgId;
      }
    }
  } catch {
    // fall through to localStorage
  }
  return localStorage.getItem("organisation_id") || "";
}

export function syncStoredOrganisationId(orgId?: string): void {
  if (!orgId) return;

  localStorage.setItem("organisation_id", orgId);

  try {
    const raw = localStorage.getItem("@user");
    if (!raw) return;

    const user = JSON.parse(raw) as StoredUser;
    user.organisation_pid = orgId;
    user.organisation_id = orgId;
    user.roles = [{ org_id: orgId }];
    localStorage.setItem("@user", JSON.stringify(user));
  } catch {
    // Keep the canonical id in localStorage even if an older user blob is malformed.
  }
}

/** Resolves display name from session storage or API user payloads. */
export function getUserDisplayName(user: unknown): string {
  if (!user || typeof user !== "object") return "";
  const record = user as Record<string, string | undefined>;
  return record.user_name || record.name || "";
}

/** Resolves display email from session storage or API user payloads. */
export function getUserDisplayEmail(user: unknown): string {
  if (!user || typeof user !== "object") return "";
  const record = user as Record<string, string | undefined>;
  return (
    record.user_email || record.email || localStorage.getItem("email") || ""
  );
}

/** Returns true when token cookie and required local session keys are present and valid. */
export function isAuthSessionValid(): boolean {
  const user = localStorage.getItem("@user");
  const orgId = localStorage.getItem("organisation_id");

  if (!user || !orgId) {
    return false;
  }

  try {
    return Boolean(getStoredUserId(JSON.parse(user)));
  } catch {
    return false;
  }
}

export function persistAuthSession(
  token: string,
  user: ApiLoginUser,
  _email?: string
): StoredUser {
  setAccessToken(token);
  const normalized = normalizeLoginUser(user);
  localStorage.setItem("@user", JSON.stringify(normalized));

  const orgId = normalized.organisation_pid || normalized.roles?.[0]?.org_id;
  if (orgId) {
    localStorage.setItem("organisation_id", orgId);
  }

  return normalized;
}

/** Clears local session storage and auth cookies. */
export function clearClientSession(): void {
  setAccessToken("");
  localStorage.clear();
  clearAuthCookies();
}

/** Clears the client session and sends the user to login. */
export function redirectToLogin(): void {
  clearClientSession();
  if (window.location.pathname !== "/login") {
    window.location.pathname = "/login";
  }
}
